import { useId } from 'react';

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  size?: 'sm' | 'md';
}

const sizes = {
  sm: {
    track: 'h-[20px] w-[36px]',
    knob: 'h-[16px] w-[16px]',
    translateOn: 'translate-x-[16px]',
  },
  md: {
    track: 'h-[24px] w-[44px]',
    knob: 'h-[20px] w-[20px]',
    translateOn: 'translate-x-[20px]',
  },
};

const SwitchButton = ({
  checked,
  onChange,
  disabled,
  size = 'md',
  id,
  labelId,
  descriptionId,
}: {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  labelId?: string;
  descriptionId?: string;
}) => {
  const s = sizes[size];

  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      className={`relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 ${s.track} ${
        checked ? 'bg-primary-600' : 'bg-gray-400'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`pointer-events-none inline-block transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${s.knob} ${
          checked ? s.translateOn : 'translate-x-0'
        }`}
      />
    </button>
  );
};

export const Toggle = ({
  checked,
  onChange,
  disabled,
  label,
  description,
  size = 'md',
}: ToggleProps) => {
  const id = useId();
  const switchId = `${id}-switch`;
  const labelId = label ? `${id}-label` : undefined;
  const descriptionId = description ? `${id}-desc` : undefined;

  if (!label) {
    return <SwitchButton checked={checked} onChange={onChange} disabled={disabled} size={size} />;
  }

  return (
    <div className="flex items-center justify-between gap-4 py-2">
      {/* A label for the switch, so the whole text block is a 44px tap target
          (the track alone is 24px tall). */}
      <label
        htmlFor={switchId}
        className={`flex min-h-[44px] flex-col justify-center ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span id={labelId} className="block text-sm font-medium text-gray-900">
          {label}
        </span>
        {description && (
          <span id={descriptionId} className="block text-xs text-gray-500">
            {description}
          </span>
        )}
      </label>
      <SwitchButton
        id={switchId}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        size={size}
        labelId={labelId}
        descriptionId={descriptionId}
      />
    </div>
  );
};
